import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { User } from "../types";
import { getPendingClassesForUser } from "../api/Users";
import { getAllProfessions, Profession } from "../api/Professions";
import { getAllStates } from "../constants/statesData";
import { professionToSlug } from "../utils/professionSlug";
import "./Navbar.css";

const PLAN_BADGE_STYLES: Record<string, { label: string; bg: string; color: string }> = {
  free:     { label: 'Free',     bg: 'rgba(255,255,255,0.15)', color: '#cbd5e1' },
  pro:      { label: 'Pro',      bg: '#f59e0b',                color: '#000'    },
  starter:  { label: 'Starter',  bg: '#0ea5e9',                color: '#fff'    },
  firm:     { label: 'Firm',     bg: '#0ea5e9',                color: '#fff'    },
  practice: { label: 'Practice', bg: '#0ea5e9',                color: '#fff'    },
};

function PlanBadge({ plan, tier, status }: { plan?: string; tier?: string; status?: string }) {
  const isTrialing = status === 'trialing';
  const key = plan === 'team' && tier ? tier : (plan || 'free');
  const style = isTrialing
    ? { label: 'Pro Trial', bg: '#f59e0b', color: '#000' }
    : (PLAN_BADGE_STYLES[key] || PLAN_BADGE_STYLES.free);
  return (
    <span style={{
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
      padding: '2px 7px',
      borderRadius: 99,
      background: style.bg,
      color: style.color,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      {style.label}
    </span>
  );
}

interface NavbarProps {
  user: User | null;
  logout: () => void;
}

const Navbar = ({ user, logout }: NavbarProps) => {
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [statesOpen, setStatesOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [navProfessions, setNavProfessions] = useState<Profession[]>([]);
  const statesRef = useRef<HTMLLIElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchPendingCount = async () => {
      if (user && user.id) {
        try {
          const res = await getPendingClassesForUser(user.id);
          setPendingCount(res.items?.length || 0);
        } catch (err) {
          console.error('Failed to fetch pending count:', err);
        }
      }
    };
    fetchPendingCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    getAllProfessions()
      .then(profs => setNavProfessions(profs.filter(p => p.isActive !== false)))
      .catch(() => {});
  }, []);

  // Hover-open behavior for dropdowns and nested state submenus
  useEffect(() => {
    const handleDropdownHover = () => {
      const mainDropdowns = document.querySelectorAll('.navbar .dropdown');

      mainDropdowns.forEach((dropdown) => {
        const toggle = dropdown.querySelector('.dropdown-toggle') as HTMLElement;
        const menu   = dropdown.querySelector('.dropdown-menu') as HTMLElement;

        if (toggle && menu) {
          const attachNestedListeners = () => {
            const dropendItems = menu.querySelectorAll('.dropend');

            dropendItems.forEach((item) => {
              const submenu = item.querySelector('.dropdown-menu') as HTMLElement;
              if (!submenu) return;

              const newItem = item.cloneNode(true) as HTMLElement;
              item.parentNode?.replaceChild(newItem, item);
              const newSubmenu = newItem.querySelector('.dropdown-menu') as HTMLElement;

              newItem.addEventListener('mouseenter', () => {
                newSubmenu.classList.add('show');

                // Dynamic left/right positioning
                const itemRect    = newItem.getBoundingClientRect();
                const spaceRight  = window.innerWidth - itemRect.right;
                if (spaceRight < 300) {
                  newSubmenu.style.left  = 'auto';
                  newSubmenu.style.right = '100%';
                  newSubmenu.style.marginLeft  = '0';
                  newSubmenu.style.marginRight = '8px';
                } else {
                  newSubmenu.style.left  = '100%';
                  newSubmenu.style.right = 'auto';
                  newSubmenu.style.marginLeft  = '8px';
                  newSubmenu.style.marginRight = '0';
                }
              });

              newItem.addEventListener('mouseleave', () => {
                newSubmenu.classList.remove('show');
              });
            });
          };

          dropdown.addEventListener('mouseenter', () => {
            menu.classList.add('show');
            toggle.setAttribute('aria-expanded', 'true');
            setTimeout(attachNestedListeners, 50);
          });

          dropdown.addEventListener('mouseleave', () => {
            menu.classList.remove('show');
            toggle.setAttribute('aria-expanded', 'false');
          });
        }
      });
    };

    const timer = setTimeout(handleDropdownHover, 100);
    return () => clearTimeout(timer);
  }, []);

  // Close states combobox on outside click or Escape
  useEffect(() => {
    if (!statesOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setStatesOpen(false); setStateSearch(''); } };
    const handleClick = (e: MouseEvent) => {
      if (statesRef.current && !statesRef.current.contains(e.target as Node)) {
        setStatesOpen(false); setStateSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [statesOpen]);

  // Auto-focus search input when combobox opens
  useEffect(() => {
    if (statesOpen) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [statesOpen]);

  const allStates = getAllStates();
  const filteredStates = stateSearch.trim()
    ? allStates.filter(s =>
        s.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
        s.abbreviation.toLowerCase().includes(stateSearch.toLowerCase())
      )
    : allStates;

  const closeNavbar = () => setMenuOpen(false);

  return (
    <nav className="navbar navbar-expand-lg navbar-dark cec-navbar">
      <div className="container">
        {/* Brand */}
        <Link className="navbar-brand d-flex align-items-center" to={user ? "/dashboard" : "/"}>
          <img
            src="/logo-square.png"
            alt="CEC Track Logo"
            height="36"
            className="d-inline-block align-top me-2"
          />
          CEC Track
        </Link>

        {/* Mobile toggle */}
        <button
          className="navbar-toggler"
          type="button"
          aria-controls="navbarNav"
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
          onClick={() => setMenuOpen(prev => !prev)}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Nav links */}
        <div className={`collapse navbar-collapse${menuOpen ? ' show' : ''}`} id="navbarNav">
          {user && (
            <ul className="navbar-nav me-auto cec-navbar-app-links">
              <li className="nav-item">
                <NavLink className="nav-link" to="/dashboard" onClick={closeNavbar}>
                  Dashboard
                  {pendingCount > 0 && (
                    <span className="badge bg-warning ms-2">{pendingCount}</span>
                  )}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/my-licenses" onClick={closeNavbar}>My Licenses</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/cycles" onClick={closeNavbar}>Cycles</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/classes" onClick={closeNavbar}>Classes</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/profile" onClick={closeNavbar}>Profile</NavLink>
              </li>
              {(user.subscription?.plan === 'team' || user.isTeamAdmin || user.role === 'admin') && (
                <li className="nav-item">
                  <NavLink className="nav-link" to="/team" onClick={closeNavbar}>Team</NavLink>
                </li>
              )}
              {user.role === 'admin' && (
                <li className="nav-item">
                  <NavLink className="nav-link" to="/admin" onClick={closeNavbar}>Admin</NavLink>
                </li>
              )}
            </ul>
          )}

          <ul className="navbar-nav ms-auto">
            {/* Professions dropdown */}
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="professionsDropdown"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Professions
              </a>
              <ul
                className="dropdown-menu dropdown-menu-end cec-professions-menu"
                aria-labelledby="professionsDropdown"
              >
                {navProfessions.map((profession) => (
                  <li key={profession.professionID}>
                    <Link
                      className="dropdown-item"
                      to={`/profession/${professionToSlug(profession.name)}`}
                      onClick={closeNavbar}
                    >
                      {profession.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>

            {/* States combobox */}
            <li ref={statesRef} className="nav-item" style={{ position: 'relative' }}>
              <button
                className="nav-link dropdown-toggle"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                aria-expanded={statesOpen}
                aria-haspopup="listbox"
                onClick={() => { setStatesOpen(prev => !prev); setStateSearch(''); }}
              >
                States
              </button>
              {statesOpen && (
                <div className="cec-states-combobox" role="dialog" aria-label="Select a state">
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="cec-states-search"
                    placeholder="Search states…"
                    value={stateSearch}
                    onChange={e => setStateSearch(e.target.value)}
                    aria-label="Search states"
                  />
                  <ul className="cec-states-list" role="listbox">
                    {filteredStates.map(state => (
                      <li key={state.stateID} role="option">
                        <Link
                          className="cec-states-option"
                          to={`/states/${state.slug}`}
                          onClick={() => { setStatesOpen(false); setStateSearch(''); closeNavbar(); }}
                        >
                          <span className="cec-state-abbr">{state.abbreviation}</span>
                          <span>{state.name}</span>
                        </Link>
                      </li>
                    ))}
                    {filteredStates.length === 0 && (
                      <li className="cec-states-no-results">No states match "{stateSearch}"</li>
                    )}
                  </ul>
                </div>
              )}
            </li>

            {/* How It Works dropdown */}
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                id="howItWorksDropdown"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                How It Works
              </a>
              <ul
                className="dropdown-menu dropdown-menu-end cec-dropdown-menu"
                aria-labelledby="howItWorksDropdown"
              >
                <li>
                  <NavLink end className="dropdown-item" to="/how-it-works" onClick={closeNavbar}>
                    Introduction
                  </NavLink>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <NavLink className="dropdown-item" to="/how-it-works/credit-distribution" onClick={closeNavbar}>
                    Credit Distribution Guide
                  </NavLink>
                </li>
              </ul>
            </li>

            {/* Pricing — always visible */}
            <li className="nav-item">
              <NavLink className="nav-link" to="/pricing" onClick={closeNavbar}>
                Pricing
              </NavLink>
            </li>

            {/* User / Logout */}
            {user ? (
              <>
                <li className="nav-item d-flex align-items-center gap-2">
                  <NavLink className="nav-link py-0" to="/billing" onClick={closeNavbar} style={{ whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
                    {user.firstName || user.email}
                  </NavLink>
                  <PlanBadge plan={user.subscription?.plan} tier={user.subscription?.tier} status={user.subscription?.status} />
                  <button
                    className="btn btn-outline-light btn-sm"
                    onClick={() => { closeNavbar(); logout(); }}
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li className="nav-item">
                <NavLink className="nav-link" to="/login" onClick={closeNavbar}>Login</NavLink>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
